import { API } from 'aws-amplify';
import { trackTiming } from '../../../utils/analytics';
import { devLog } from '../../../utils/devLog';
import { isEmpty, noop } from '../../../utils/helpers';
import { safeParse } from '../../../utils/stringHelpers';
import { graphQLQuery } from '../../api/ApiClient';
import { genericRequest } from '../../graphql/mutations';
import store from '../../store';
import * as type from '../types';

export const handleSuccess = (action, model, message, data, variables, dispatch) => {
  devLog('success', model, data);

  if (message) {
    dispatch({
      type: type.ADD_MESSAGE,
      payload: {
        id: `${action}_${new Date().getTime()}`,
        message: `Success, ${message}`,
        severity: 'success',
      },
    });
  }

  dispatch({
    type: `${action}_SUCCESS`,
    payload: { data, meta: { variables } },
  });
};

export const handleError = (action, model, message, error, dispatch, arg = {}) => {
  devLog('error', model, error);

  dispatch({
    type: type.ADD_MESSAGE,
    payload: {
      id: `${action}_${new Date().getTime()}`,
      message: `Oh no, ${message}`,
      severity: 'error',
      context: `${error}`,
      model,
      arg,
    },
  });

  dispatch({
    type: `${action}_FAILURE`,
    payload: message,
  });
};

export const beginAction = (action, loadingId, cancel, dispatch) => {
  dispatch({
    type: type.SET_ID_LOADING,
    payload: { id: loadingId, cancel },
  });

  dispatch({
    type: `${action}_PENDING`,
  });
};

export const endAction = (loadingId, dispatch) => {
  dispatch({
    type: type.REMOVE_ID_LOADING,
    payload: loadingId,
  });
};

export const cancelPendingApiAction = (action, now) => {
  const state = store?.getState();
  const { loading } = state;

  loading.forEach((request) => {
    if (request.id.startsWith(action) && now > parseInt(request.id.replace(`${action}_`, ''), 10)) {
      devLog('info', 'cancelling pending request', request);

      request.cancel();
    }
  });
};

/**
 * Create an API action that runs the response through redux
 *
 * @param action
 * @param request
 * @param apiVariables
 * @param model
 * @param errorMessage
 * @param dispatch
 * @param successMessage
 * @param actionResponse
 * @param cancelPending
 * @param includeRestId
 * @param throwErrorOnNull
 * @param onSuccess
 * @param isGenericRequest
 * @param extractModel
 */
export const makeApiAction = (
  action,
  request,
  apiVariables = {},
  model,
  errorMessage,
  dispatch,
  successMessage = null,
  actionResponse = null,
  cancelPending = false,
  includeRestId = false,
  throwErrorOnNull = false,
  onSuccess = noop, // success callback
  isGenericRequest = false,
  extractModel = null, // To extract the model property generic requests due to api inconsistencies
) => {
  const now = new Date().getTime();
  const loadingId = `${action}_${now}`;

  if (cancelPending) {
    cancelPendingApiAction(action, now);
  }

  const state = store.getState();
  const restId = state.restaurant.data?.objectId;

  let variables = apiVariables;
  if (includeRestId) {
    if (isEmpty(restId)) {
      devLog('error', 'makeApiAction', 'restId is undefined');
      return;
    }

    variables = { ...variables, restId };
  }

  devLog('info', `preparing makeApiAction`, { variables });

  const promise = request(variables);

  beginAction(action, loadingId, () => API.cancel(promise), dispatch);

  const extractResponseData = (res) => {
    if (isGenericRequest) {
      if (extractModel) {
        return safeParse(res?.data?.genericRequest)?.[model];
      }
      return safeParse(res?.data?.genericRequest);
    }

    return res.data?.[model];
  };

  (async () => {
    try {
      const response = await promise;
      trackTiming(model, new Date().getTime() - now);

      const responseData = extractResponseData(response);

      const isEmptyResponse = isGenericRequest ? isEmpty(responseData) : isEmpty(responseData);

      if (throwErrorOnNull && isEmptyResponse) {
        devLog('error', 'makeApiAction', `${model} returned null`);
        throw new Error(`${model} returned null`);
      }

      handleSuccess(
        action,
        model,
        successMessage,
        actionResponse || responseData,
        variables,
        dispatch,
      );

      onSuccess(responseData);
    } catch (error) {
      if (API.isCancel(error)) {
        devLog('info', 'cancelled pending request', `${action}_${now}`);
      } else {
        devLog('info', 'error with request', `${action}_${now}`, error);
        const errorMessageToShow = errorMessage || error?.errors?.[0]?.message || error;
        handleError(action, model, errorMessageToShow, error, dispatch, variables);
      }
    } finally {
      endAction(loadingId, dispatch);
    }
  })();
};

/**
 * Pass through a request that hits the generic api endpoint that does a passthrough in graphql.
 * This is a workaround so not every new endpoint needs to be added to the schema.
 * Look at the Obee endpoint documentation to make sure you are using the endpoint correctly
 *
 * @param action
 * @param model
 * @param errorMessage
 * @param dispatch
 * @param successMessage
 * @param actionResponse
 * @param cancelPending
 * @param includeRestId
 * @param throwErrorOnNull
 * @param onSuccess
 * @param method
 * @param path
 * @param payload
 * @returns {*}
 */
export const makeGenericApiAction = ({
  action,
  model,
  errorMessage,
  dispatch,
  successMessage = null,
  actionResponse = null,
  cancelPending = true,
  throwErrorOnNull = false,
  onSuccess = noop, // success callback
  method,
  path,
  payload,
  extractModel = false,
}) => {
  const request = (requestVariables) => graphQLQuery(genericRequest, requestVariables);

  const variables = {
    method,
    path,
  };

  if (!isEmpty(payload)) {
    variables.payload = JSON.stringify(payload);
  }

  return makeApiAction(
    action,
    request,
    variables,
    model,
    errorMessage,
    dispatch,
    successMessage,
    actionResponse,
    cancelPending,
    true,
    throwErrorOnNull,
    onSuccess,
    true,
    extractModel,
  );
};

/**
 * Make an API call, separate from the actions
 */
export const makeGenericApiCall = async (method, path, payload) => {
  const variables = {
    method,
    path,
  };

  if (!isEmpty(payload)) {
    variables.payload = JSON.stringify(payload);
  }

  const state = store.getState();
  const restId = state.restaurant.data?.objectId;

  if (isEmpty(restId)) {
    devLog('error', 'genericApiCall', 'restId is undefined');
    return null;
  }

  variables.restId = restId;

  devLog('info', `preparing makeApiAction`, { variables });

  try {
    const request = await graphQLQuery(genericRequest, variables);
    const responseData = safeParse(request.data.genericRequest);
    const voucherData = responseData.voucher;

    return voucherData;
  } catch (e) {
    return {
      valid: false,
      reason: 'Unable to redeem voucher',
    };
  }
};
